import React from "react";
import { motion } from "framer-motion";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Copy,
  Check,
  Eye,
} from "lucide-react";
import StatusBadge from "./StatusBadge";
import { Badge } from "@/shared/components/ui/badge";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { RowTimer } from "../utilities/rowtimer";
import utilities from "@/shared/utils/utilities";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import TableSkeleton from "@/shared/components/shared/TableSkeleton";

export default function TransactionTable({
  transactions = [],
  isLoading,
  onRowClick,
  compact = false,
  hideViewButton = false,
}) {
  const { t, language = "en" } = useLanguage() || {};
  const translate = (key) => (t ? t(key) : key);

  const [copiedRowKey, setCopiedRowKey] = React.useState(null);

  const columns = React.useMemo(() => {
    const cols = [
      {
        id: "transaction",
        header: translate("transaction"),
        width: "200px",
      },
      {
        id: "provider",
        header: translate("provider"),
        className: "text-center",
      },
      {
        id: "amount",
        header: translate("amount"),
        className: "text-center",
      },
      {
        id: "fee",
        header: "Commission Fee",
        className: "text-center",
      },
      {
        id: "netAmount",
        header: translate("netAmount") || "Net Amount",
        className: "text-center",
      },
      {
        id: "currency",
        header: translate("currency") || "Currency",
        className: "text-center",
      },
      {
        id: "status",
        header: translate("status"),
        className: "text-center",
      },
      {
        id: "date",
        header: translate("date"),
        className: "text-center",
      },
      !hideViewButton && {
        id: "view",
        header: "",
        className: "text-right",
      },
    ];
    return cols.filter(Boolean);
  }, [t, hideViewButton]);

  const isDeposit = (tx) => String(tx?.type ?? "").toLowerCase() === "deposit";

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <TableSkeleton columns={columns} rows={8} />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
      <div className="overflow-x-auto scrollbar-thin">
        <Table className="w-full min-w-[900px]">
          <TableHeader>
            <TableRow className="bg-slate-50/50 border-b border-slate-100 hover:bg-transparent">
              {columns.map((col) => (
                <TableHead
                  key={col.id}
                  className={`h-11 py-0 font-bold text-slate-500 text-[11px] uppercase tracking-wider whitespace-nowrap ${col.className || ""}`}
                  style={col.width ? { width: col.width } : {}}
                >
                  {col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {transactions.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="text-center py-20 text-slate-400 font-medium"
                >
                  <div className="flex flex-col items-center gap-2">
                    <p className="text-lg text-slate-300">No Transactions Found</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              transactions.map((tx, index) => (
                <motion.tr
                  key={tx.id || index}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.02 * index }}
                  onClick={() => onRowClick?.(tx)}
                  className="hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-50 last:border-0"
                >
                  <TableCell className="py-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                          isDeposit(tx) ? "bg-blue-50" : "bg-amber-50"
                        }`}
                      >
                        {isDeposit(tx) ? (
                          <ArrowDownToLine className="h-4 w-4 text-blue-600" />
                        ) : (
                          <ArrowUpFromLine className="h-4 w-4 text-amber-600" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1">
                          <p className="font-mono text-[11px] font-medium text-slate-500 truncate block max-w-[100px]">
                            {tx.id?.slice(0, 12)}
                          </p>
                          <button
                            type="button"
                            onClick={(e) =>
                              utilities.copyToClipboard(
                                e,
                                tx.id,
                                setCopiedRowKey,
                              )
                            }
                            className={`p-1 rounded-md transition-all ${
                              copiedRowKey === tx.id
                                ? "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100"
                                : "text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                            }`}
                          >
                            {copiedRowKey === tx.id ? (
                              <Check className="h-3 w-3" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </button>
                        </div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight mt-0.5">
                          {utilities.getTypeLabel(tx.type, t)}
                        </p>
                      </div>
                    </div>
                  </TableCell>

                  <TableCell className="py-3 text-center">
                    <span className="text-sm text-slate-600 font-medium">
                      {tx.provider_display || tx.provider_code || "—"}
                    </span>
                  </TableCell>

                  <TableCell className="py-3 text-center">
                    <span className="font-bold text-slate-900">
                      {utilities.getCurrencySymbol(tx.currency)} {tx.amount}
                    </span>
                  </TableCell>

                  <TableCell className="py-3 text-center">
                    <span className="font-medium text-red-500">
                      {utilities.getCurrencySymbol(tx.currency)} {tx.fee_amount}
                    </span>
                  </TableCell>

                  <TableCell className="py-3 text-center">
                    <span className="font-bold text-emerald-600">
                      {utilities.getCurrencySymbol(tx.currency)}{" "}
                      {tx.net_amount}
                    </span>
                  </TableCell>

                  <TableCell className="py-3 text-center">
                    <Badge
                      variant="outline"
                      className="text-[10px] font-bold uppercase border-slate-200 text-slate-500 bg-slate-50/50"
                    >
                      {tx.currency}
                    </Badge>
                  </TableCell>

                  <TableCell className="py-3 text-center">
                    <StatusBadge status={tx.status} />
                  </TableCell>

                  <TableCell className="py-3 text-center">
                    <div className="flex flex-col items-center">
                      <span className="text-[13px] font-medium text-slate-700 whitespace-nowrap">
                        {utilities.formatDate(
                          tx.createdAt || tx.created_date,
                          language,
                        )}
                      </span>
                      <RowTimer
                        createdAt={tx.createdAt || tx.created_date}
                        status={tx.status}
                      />
                    </div>
                  </TableCell>

                  {!hideViewButton && (
                    <TableCell className="py-3 text-right">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRowClick?.(tx);
                        }}
                        className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </TableCell>
                  )}
                </motion.tr>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

